/**
 * One visual language for the Live page, taken from the approved style demo
 * (tools/cpwealth_dashboard/demo/style_demo.html?s=assertion) and ultimately from
 * src/theme.js. Both grids import these so there is exactly one place a padding
 * or a border colour is defined — the page previously read as "a bit of
 * everything" because each section styled itself.
 */

export const BRAND = "#2DA894";
export const BRAND_L = "#DFF0EE";
export const BRAND_D = "#1E7F6E";
export const ERR = "#EA4335";
export const HAIR = "#EEF0F2";
export const HEAD_BG = "#F4FAF8";
export const ROW_HOVER = "#F7FAF9";

/* One size for every "N of M rows" line on the page, so no two sections
   disagree about how big a row count is. */
export const META_SIZE = ".75rem";

/** The 3px brand rule along the top of every section card. */
export const brandTop = {
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "0 0 auto",
    height: 3,
    background: `linear-gradient(90deg,${BRAND},#5ec9b8 55%,#5E97F6)`,
    zIndex: 2,
  },
};

/** Section card: white, soft, rounded, with the brand rule. */
export const cardSx = {
  ...brandTop,
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  bgcolor: "#fff",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 1px 2px rgba(29,41,59,.04), 0 1px 10px rgba(29,41,59,.03)",
  px: 2.5,
  pt: 2.25,
  pb: 2.25,
};

/**
 * Skin for the two sections this page does not own.
 *
 * BusinessDataSourcePanel and BusinessRecommendation render their own cards and
 * headings, and BOTH are shared with Notebook — so restyling them at the source
 * would change a page nobody asked about. This applies the Live page's language
 * from the OUTSIDE, via descendant selectors on the wrapper.
 *
 * Works because `.wrapper .MuiTypography-h6` (two classes) outranks the element's
 * own emotion class (one class), so these win without `!important`.
 *
 * Every selector below was read off the actual rendered DOM through
 * pages/__LivePreview.jsx. An earlier version guessed at `h4.MuiTypography-subtitle2`
 * for the section headings and silently hit nothing — the headings are
 * `variant="h6" component="h2"` inside the component's own SectionCard.
 */
export const legacySectionSkin = {
  // The wrapper becomes the single section card...
  ...cardSx,
  // ...and the component's own stack of outlined cards is flattened into it, which
  // is the structural difference: it renders one card per block, the design is one
  // card with sub-headings.
  /* Alerts are Paper too, and flattening them stripped the tint off the warning
     and error states — an error message with no red background reads as body copy.
     Excluded explicitly. */
  "& .MuiPaper-root:not(.MuiAlert-root), & .MuiCard-root": {
    border: 0, boxShadow: "none", backgroundColor: "transparent", borderRadius: 0,
    backgroundImage: "none",
  },
  "& .MuiAlert-root": { borderRadius: "8px" },
  "& .MuiCardContent-root": { padding: 0, "&:last-child": { paddingBottom: 0 } },

  /* The hero is a plain Box with its own tint, border and heavy padding — not a
     Paper, so the flatten above misses it. Selected by the one thing unique to it:
     it is the only element whose direct child is an h6 rendered as a <div> (the
     section headings use component="h2"). Needs :has(), which every browser this
     app supports has had for a while. */
  "& .MuiBox-root:has(> div.MuiTypography-h6)": {
    background: "none", backgroundColor: "transparent", border: 0,
    borderRadius: 0, padding: 0,
  },

  // Hero: label acts as the card title, statement directly under it.
  "& div.MuiTypography-h6": {
    fontSize: "1rem", fontWeight: 600, letterSpacing: "-.01em", color: "text.primary",
  },
  "& h1.MuiTypography-h5": {
    fontSize: "1rem", fontWeight: 600, letterSpacing: "-.01em", lineHeight: 1.5,
    marginTop: "10px", color: "text.primary",
  },

  // Confidence / Drivers / Risks / Proof — the demo's uppercase rule, not a title.
  "& h2.MuiTypography-h6": {
    fontSize: ".75rem", fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase",
    color: BRAND_D, borderBottom: `2px solid ${BRAND_L}`,
    paddingBottom: "6px", marginBottom: "12px",
  },
  // Data source panel's own title.
  "& .MuiTypography-subtitle1": {
    fontSize: "1rem", fontWeight: 600, letterSpacing: "-.01em", color: "text.primary",
  },
  // The small "Description" / "Metrics" / "Features" micro-labels.
  "& .MuiTypography-overline, & .MuiTypography-caption": { fontSize: ".75rem" },

  // Tables are real MUI Tables here (GenericObjectTable), so they can carry the
  // same header treatment as the two native grids above.
  "& .MuiTableContainer-root": { maxWidth: "none", borderRadius: 0, marginTop: "8px" },
  "& .MuiTableCell-head": {
    fontSize: ".75rem", fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase",
    color: BRAND_D, backgroundColor: HEAD_BG,
    borderBottom: `2px solid ${BRAND_L}`, padding: "8px 12px",
  },
  "& .MuiTableCell-root": {
    fontSize: ".875rem", padding: "7px 12px", borderBottom: `1px solid ${HAIR}`,
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: ROW_HOVER },

  "& .MuiTabs-root": { minHeight: 0, borderBottom: "1px solid", borderColor: "divider" },
  "& .MuiTabs-indicator": { backgroundColor: BRAND, height: 2 },
  "& .MuiTab-root": {
    textTransform: "none", fontSize: ".875rem", fontWeight: 600, minHeight: 0,
    padding: "8px 14px", marginRight: "4px", borderRadius: "6px 6px 0 0",
    "&:hover": { backgroundColor: ROW_HOVER },
    "&.Mui-selected": { color: BRAND_D, backgroundColor: BRAND_L },
  },
  "& .MuiButton-root": { textTransform: "none", fontWeight: 600, borderRadius: "6px" },
  "& .MuiButton-contained": { boxShadow: "none" },
  "& .MuiTypography-body1, & .MuiTypography-body2": { fontSize: ".875rem" },
  /* RunDataView's own "N of M rows" is no longer skinned from here: it now renders
     below its grid as a caption, which is already META_SIZE. A positional selector
     would have silently stopped matching when it moved. */
  "& ul": { paddingLeft: "20px", marginTop: "6px" },
  "& li": { fontSize: ".875rem", marginTop: "4px", marginBottom: "4px" },
};

export const titleSx = { fontSize: "1rem", fontWeight: 600, letterSpacing: "-.01em", color: "text.primary" };
export const capSx = { fontSize: ".75rem", color: "text.secondary", display: "block" };
/** Row counts sit BELOW their table. */
export const countSx = {
  fontSize: META_SIZE, color: "text.secondary", display: "block",
  mt: 1.25,
};

/** The vintage sits ABOVE its table, where it qualifies what you are about to read
    rather than being a footnote after it. */
export const vintageSx = {
  fontSize: META_SIZE, color: "text.secondary", display: "block",
  mt: 1.25, mb: 0.75,
};

export const tabsSx = {
  minHeight: 0, mt: 1.75,
  borderBottom: "1px solid", borderColor: "divider",
  "& .MuiTabs-indicator": { backgroundColor: BRAND, height: 2 },
  "& .MuiTab-root": {
    textTransform: "none", fontSize: ".875rem", fontWeight: 600, minHeight: 0,
    px: 1.75, py: 1, mr: 0.5, borderRadius: "6px 6px 0 0", color: "text.secondary",
    "&:hover": { backgroundColor: ROW_HOVER },
    "&.Mui-selected": { color: BRAND_D, backgroundColor: BRAND_L },
  },
};

export const exportButtonSx = {
  textTransform: "none", fontWeight: 600, fontSize: ".875rem",
  borderRadius: "6px", boxShadow: "none", whiteSpace: "nowrap",
  backgroundColor: BRAND, "&:hover": { backgroundColor: BRAND_D, boxShadow: "none" },
};

/**
 * All cell styling lives on the scroll container via descendant selectors, so
 * table bodies can be plain <tr>/<td>. Styling cells individually with `sx`
 * would allocate an emotion style per cell — 200 rows x 20 columns is 4,000 per
 * chunk — which the 6,610-row Detail view cannot afford.
 */
export const scrollSx = (maxHeight = 460) => ({
  overflow: "auto",
  maxHeight,
  "& table": { borderCollapse: "collapse", width: "100%", minWidth: "max-content" },
  "& th": {
    position: "sticky", top: 0, zIndex: 1, cursor: "pointer", userSelect: "none",
    backgroundColor: HEAD_BG, color: BRAND_D,
    fontSize: ".75rem", fontWeight: 600, letterSpacing: ".05em", textTransform: "uppercase",
    textAlign: "left", whiteSpace: "nowrap", px: 1.5, py: 1,
    borderBottom: `2px solid ${BRAND_L}`,
  },
  "& th[aria-sort]": { backgroundColor: BRAND_L },
  "& th.n, & td.n": { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  "& td": {
    px: 1.5, py: 0.875, fontSize: ".875rem", whiteSpace: "nowrap",
    borderBottom: `1px solid ${HAIR}`,
  },
  "& tbody tr:hover": { backgroundColor: ROW_HOVER },
  "& tbody tr.avoid": { backgroundColor: "rgba(234,67,53,.06)" },
  "& tbody tr.avoid:hover": { backgroundColor: "rgba(234,67,53,.10)" },
  "& td.empty": { textAlign: "center", py: 4, color: "text.secondary" },
  "& .arw": { ml: 0.5, fontSize: 9, opacity: 0.3 },
  "& th[aria-sort] .arw": { opacity: 1 },
});

/** Sort arrow + aria-sort for a header, shared so both grids behave alike. */
export function headerSortProps(key, idx) {
  const on = key && key.idx === idx;
  return {
    "aria-sort": on ? (key.dir === "asc" ? "ascending" : "descending") : undefined,
    arrow: on && key.dir === "desc" ? "▼" : "▲",
  };
}

/** Blanks last in BOTH directions — the Power BI behaviour the report matched. */
export function sortRows(rows, isNumeric, key) {
  if (!key) return rows;
  const { idx, dir } = key;
  const num = isNumeric(idx);
  return rows.slice().sort((a, b) => {
    const x = a[idx];
    const y = b[idx];
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    const c = num ? x - y : String(x).localeCompare(String(y));
    return dir === "asc" ? c : -c;
  });
}

export function nextSort(cur, idx) {
  return cur && cur.idx === idx
    ? { idx, dir: cur.dir === "asc" ? "desc" : "asc" }
    : { idx, dir: "asc" };
}
